import { useCallback, useEffect, useMemo, useState } from 'react'

import { OnboardingResponseStatus } from 'clickNgo/configs/service/onboarding'
import { IContact, TContactGeneralForm } from 'clickNgo/entities/contact'
import {
  searchPortalContact,
  TSearchPortalContact,
  validateContact,
} from 'clickNgo/entities/contact/api'
import { PartnerHierarchyType } from 'clickNgo/entities/partner/configs/index'
import { IPartner } from 'clickNgo/entities/partner/models'
import { getJSendErrorMessages } from 'clickNgo/helpers/api'
import { useGetFormData, useGetFormDataField } from 'cng-common/features/form'
import { validateFormData } from 'cng-common/features/form/helpers'
import { Nullable } from 'cng-common/models/utils'
import { TWizardStep } from 'shared/components/wizard'

import {
  ContactCreatorFormKeys,
  ContactCreatorSteps,
  CreateContactFormContent,
} from '../../../../configs'
import { IContactFormKeys } from '../../../../models'
import { GeneralStep } from '../GeneralStep'

import { SearchDuplicateResult } from './models'
import { useGeneralStepValidation } from './useGeneralStepValidation'

interface IParams {
  formKeys: IContactFormKeys
  contact: IContact
  partner: IPartner
  onReactivate: (contact: IContact) => void
}

export const useGeneralStep = ({ contact, formKeys, onReactivate, partner }: IParams) => {
  const [{ formData }] = useGetFormData<TContactGeneralForm>(ContactCreatorFormKeys.General)
  const [, setErrorMessages] = useGetFormDataField<string[]>(
    ContactCreatorFormKeys.Settings,
    CreateContactFormContent.fields.errorMessages.field
  )

  const validationProps = useGeneralStepValidation()
  const [duplicate, setDuplicate] = useState<Nullable<TSearchPortalContact>>(null)
  const [isDuplicateReactivated, setIsDuplicateReactivated] = useState(false)

  const isOnboardingRequest = [
    PartnerHierarchyType.AFFILIATE,
    PartnerHierarchyType.RESELLER_NETWORK,
  ].includes(partner.hierarchyType)

  const hasDuplicate = useMemo(() => duplicate !== null, [duplicate])

  const searchDuplicate = useCallback(
    async (email: string): Promise<SearchDuplicateResult> => {
      const response = await searchPortalContact(email)

      if (response.status === OnboardingResponseStatus.Error) {
        setErrorMessages([`Search Portal: ${response.message || 'unknown error'}`])
        return { status: 'error' }
      }

      // ONB-4527 when email address submitted from React forms = with email address existing under same partner from the request, the duplication must not be displayed in the ‘List of Contacts’ form
      const isExistingContactAllowedForRequest =
        Boolean(contact.onboardingRequestId) &&
        response.data.contact?.partnerId === partner.partnerId

      if (response.data.contact !== null && !isExistingContactAllowedForRequest) {
        setDuplicate(response.data.contact)
      }

      return {
        status: 'ok',
        contact: isExistingContactAllowedForRequest ? null : response.data.contact,
      }
    },
    [contact.onboardingRequestId, partner.partnerId, setErrorMessages]
  )

  const onReactivationComplete = useCallback(
    async (contact: IContact) => {
      setIsDuplicateReactivated(true)
      onReactivate(contact)
    },
    [onReactivate]
  )

  const handleOnStepChange = useCallback(async () => {
    setErrorMessages([])

    const validationResult = await validateFormData(formData, ContactCreatorFormKeys.General)

    if (validationResult && validationResult.length > 0) {
      return {
        hasError: true,
      }
    }

    const validateAsyncResult = await validateContact({
      contactId: contact.contactId,
      partnerId: contact.partnerId,
      country: partner.country,
      ...formData,
    })

    if (validateAsyncResult.status === OnboardingResponseStatus.Fail) {
      setErrorMessages(getJSendErrorMessages(validateAsyncResult))
      return {
        hasError: true,
      }
    }

    if (validateAsyncResult.status === OnboardingResponseStatus.Error) {
      setErrorMessages([validateAsyncResult.message])
      return {
        hasError: true,
      }
    }

    if (isOnboardingRequest) {
      return
    }

    const foundDuplicate = await searchDuplicate(formData.email)

    if (foundDuplicate.status === 'error') {
      return { hasError: true }
    }

    if (foundDuplicate.status === 'ok' && foundDuplicate.contact) {
      return { hasError: true }
    }

    return
  }, [
    formData,
    isOnboardingRequest,
    contact.contactId,
    contact.partnerId,
    partner.country,
    searchDuplicate,
    setErrorMessages,
  ])

  const renderContent = useCallback(
    () => (
      <GeneralStep
        contact={contact}
        partner={partner}
        formKeys={formKeys}
        duplicate={duplicate}
        onReactivationComplete={onReactivationComplete}
        isDuplicateReactivated={isDuplicateReactivated}
        {...validationProps}
      />
    ),
    [
      contact,
      partner,
      formKeys,
      duplicate,
      onReactivationComplete,
      isDuplicateReactivated,
      validationProps,
    ]
  )

  const generalStep = useMemo<TWizardStep>(
    () => ({
      id: ContactCreatorSteps.General,
      title: ContactCreatorSteps.General,
      content: renderContent,
      onStepChange: handleOnStepChange,
      isNextStepDisabled: hasDuplicate,
    }),
    [handleOnStepChange, hasDuplicate, renderContent]
  )

  useEffect(() => {
    setDuplicate(null)
  }, [formData.email])

  return { generalStep }
}
